/**
 * Bets domain — TanStack Query keys, fetchers.
 *
 * The /bets endpoint is member-scoped via the JWT (agentContextPlugin uses
 * auth.sub as memberId filter). Frontend passes memberId explicitly to
 * tighten the server-side scope further.
 */
import { apiFetch } from '../../lib/api/client.js';
import { useAuthStore } from '../../stores/auth.js';

export const BETS_QUERY_KEYS = {
  list: (params: { providerId?: string; status?: string; from?: string; to?: string }) =>
    ['bets', 'list', params] as const,
  // The date range is part of the key: it is a SERVER filter now, so two ranges
  // must not share a cache entry (WEB-23).
  infinite: (params: { providerId?: string; status?: string; from?: string; to?: string }) =>
    ['bets', 'infinite', params] as const,
} as const;

export interface Bet {
  id: string;
  memberId: string;
  providerId: string;
  gameId: string | null;
  gameType: string;
  stake: number;
  payout: number | null;
  status: 'placed' | 'settled' | 'cancelled' | 'void' | 'payout_cancelled';
  currency: string;
  providerBetId: string;
  providerRoundId: string | null;
  createdAt: string;
  updatedAt: string;
  providerDisplayName?: string;
  gameName?: string;
}

export interface BetPage {
  items: Bet[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchBets(params: {
  page?: number;
  pageSize?: number;
  providerId?: string;
  status?: string;
  from?: string;
  to?: string;
}): Promise<BetPage> {
  const qs = new URLSearchParams();
  const memberId = useAuthStore.getState().subject?.id;
  if (memberId) qs.set('memberId', memberId);
  qs.set('pageSize', String(params.pageSize ?? 20));
  if (params.page) qs.set('page', String(params.page));
  if (params.providerId) qs.set('providerId', params.providerId);
  if (params.status) qs.set('status', params.status);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  qs.set('order', 'desc');
  return apiFetch<BetPage>(`/bets?${qs.toString()}`);
}
